// ds-typo-ignore-file — overlay text mirrors Figma spec sizes (8–12px micro
// typography) meant to read at the displayed mockup scale, not the marketing
// type tokens.
"use client"

/**
 * Figma 193:2106 — overlay card on the biomarkers "Included" grid tile.
 * Light glass (white/60) with dark text, a segmented colour bar and white
 * chips. Shared by the membership "What's included" section and the
 * ambassadors grid (previously two byte-identical copies). Counts come from
 * `BIOMARKER_SAMPLE`; the bar derives its widths via `SegmentedBar`.
 */

import { useTranslations } from "@/lib/intl/use-translations"
import { membershipKeys as mk } from "@/i18n/membership-keys"
import { SegmentedBar } from "@/components/ui/segmented-bar"
import { BIOMARKER_SAMPLE, BIOMARKER_SAMPLE_TOTAL } from "./biomarker-sample"

export function BiomarkerOverlay() {
  const t = useTranslations("_membership") as unknown as (key: string) => string
  const chips = [
    t(mk.overlayBiomarkerChipOptimal),
    t(mk.overlayBiomarkerChipNormal),
    t(mk.overlayBiomarkerChipOutOfRange),
  ]
  return (
    <div className="pointer-events-none absolute top-1/2 left-1/2 flex w-[86%] -translate-x-1/2 -translate-y-1/2 flex-col gap-[10px] rounded-[10px] border-[0.4px] border-white/40 bg-white/60 p-[10px] backdrop-blur-[6.905px]">
      <div className="flex flex-col whitespace-nowrap">
        <span className="text-[28px] leading-[32px] font-normal tracking-[-0.25px] text-neutral-800">
          {BIOMARKER_SAMPLE_TOTAL}
        </span>
        <span className="text-[9px] leading-[12px] text-neutral-600/80">
          {t(mk.overlayBiomarkerLabelPlural)}
        </span>
      </div>
      <SegmentedBar
        height={4}
        gap={2}
        animate={false}
        segments={[
          {
            value: BIOMARKER_SAMPLE.optimal,
            colorClassName: "bg-deep-green-500",
          },
          {
            value: BIOMARKER_SAMPLE.normal,
            colorClassName: "bg-light-green-500",
          },
          {
            value: BIOMARKER_SAMPLE.outOfRange,
            colorClassName: "bg-terracotta-500",
          },
        ]}
      />
      {/* Wraps instead of truncating: at 8px the three chips fit on one line in
        English ("12 optimal", "67 in range", "2 out of range") but not in the
        longer languages, where "67 im Normbereich" used to render "67 im N…"
        and lose the word that gives the number its meaning. Wrapping costs one
        line of the card's own padding; clipping cost the sentence. */}
      <ul className="flex flex-wrap items-center gap-[3px]">
        {chips.map((label) => (
          <li
            key={label}
            className="inline-flex items-center rounded-[90px] bg-white px-[6px] py-[2px]"
          >
            <span className="text-[8px] leading-[10px] font-medium tracking-[0.05px] whitespace-nowrap text-neutral-800/70">
              {label}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}
